import {
  BAG_LIMIT,
  GEAR_SLOTS,
  MAX_ENHANCE,
  RARITIES,
  type Currencies,
  type EquippedGear,
  type GearSlot,
  type Item,
  type Rarity,
  type SaveData,
} from '../model/types.ts';

const RARITY_RANK: Record<Rarity, number> = {
  common: 0,
  uncommon: 1,
  rare: 2,
  epic: 3,
  legendary: 4,
};

export function itemScore(item: Item): number {
  const m = 1 + item.enhance * 0.08;
  const s = item.stats;
  return (s.hp * 0.35 + s.attack * 3 + s.armor * 2 + s.armorBreak * 2.5 + s.heal * 2) * m;
}

export function enhanceCost(item: Item): { gold: number; forgeDust: number } {
  const step = item.enhance + 1;
  return { gold: 8 * item.tier * step, forgeDust: item.tier * step };
}

export function enhanceItem(
  item: Item,
  wallet: Currencies,
): { ok: true; item: Item; wallet: Currencies } | { ok: false; reason: string } {
  if (item.enhance >= MAX_ENHANCE) return { ok: false, reason: 'Already +10.' };
  const cost = enhanceCost(item);
  if (wallet.gold < cost.gold || wallet.forgeDust < cost.forgeDust) {
    return { ok: false, reason: 'Not enough gold or forge dust.' };
  }
  return {
    ok: true,
    item: { ...item, enhance: item.enhance + 1 },
    wallet: {
      gold: wallet.gold - cost.gold,
      forgeDust: wallet.forgeDust - cost.forgeDust,
    },
  };
}

const SALVAGE_MULT: Record<Rarity, number> = {
  common: 1,
  uncommon: 2,
  rare: 4,
  epic: 7,
  legendary: 12,
};

export function salvageYield(item: Item): number {
  return item.tier * SALVAGE_MULT[item.rarity] * (1 + item.enhance);
}

export function addToBag(
  bag: Item[],
  incoming: Item[],
  limit = BAG_LIMIT,
): { bag: Item[]; salvaged: Item[]; dust: number; gold: number } {
  const combined = [...bag, ...incoming];
  if (combined.length <= limit) return { bag: combined, salvaged: [], dust: 0, gold: 0 };
  const protectedItems = combined.filter((item) => !canRecycle(item));
  const open = combined.filter((item) => canRecycle(item));
  open.sort((a, b) => itemScore(b) - itemScore(a) || RARITY_RANK[b.rarity] - RARITY_RANK[a.rarity]);
  const room = Math.max(0, limit - protectedItems.length);
  const keepOpen = open.slice(0, room);
  const salvaged = open.slice(room);
  let dust = 0;
  let gold = 0;
  for (const item of salvaged) {
    const reward = recycleReward(item);
    dust += reward.forgeDust;
    gold += reward.gold;
  }
  const keepIds = new Set([...protectedItems, ...keepOpen].map((item) => item.id));
  return { bag: combined.filter((item) => keepIds.has(item.id)), salvaged, dust, gold };
}

/** Boss signature pieces stay, whether or not a set is shipped yet. */
export function isBossSetPiece(item: Item): boolean {
  if (item.isBossSet === true) return true;
  return typeof item.setId === 'string' && item.setId.length > 0;
}

/** Equipped, locked, and boss-set pieces are never recycled. */
export function canRecycle(item: Item, equipped?: EquippedGear): boolean {
  if (equipped && GEAR_SLOTS.some((slot) => equipped[slot]?.id === item.id)) return false;
  if (item.locked === true) return false;
  if (isBossSetPiece(item)) return false;
  return true;
}

const RECYCLE_GOLD: Record<Rarity, number> = {
  common: 1,
  uncommon: 2,
  rare: 3,
  epic: 5,
  legendary: 8,
};

/** Forge dust when salvage already pays it. Otherwise a small gold purse. */
export function recycleReward(item: Item): { gold: number; forgeDust: number } {
  const forgeDust = salvageYield(item);
  if (forgeDust > 0) return { gold: 0, forgeDust };
  return { gold: item.tier * RECYCLE_GOLD[item.rarity], forgeDust: 0 };
}

export function recycleGoldFallback(item: Item): number {
  return item.tier * RECYCLE_GOLD[item.rarity];
}

export function rarityRank(rarity: Rarity): number {
  return RARITY_RANK[rarity];
}

export function sortBag(items: Item[], direction: 'high' | 'low'): Item[] {
  const sign = direction === 'high' ? -1 : 1;
  return [...items].sort((a, b) => {
    const rarity = RARITY_RANK[a.rarity] - RARITY_RANK[b.rarity];
    if (rarity) return rarity * sign;
    const tier = a.tier - b.tier;
    if (tier) return tier * sign;
    return (a.enhance - b.enhance) * sign;
  });
}

export function autoRecycleLine(data: SaveData): Rarity | null {
  const line = data.gear.autoRecycleBelow;
  if (!line || !RARITIES.includes(line)) return null;
  return line;
}

export function previewRecycle(data: SaveData, below: Rarity): { count: number; gold: number; forgeDust: number } {
  return data.gear.bag.reduce(
    (sum, item) => {
      if (RARITY_RANK[item.rarity] >= RARITY_RANK[below] || !canRecycle(item, data.gear.equipped)) return sum;
      const reward = recycleReward(item);
      return { count: sum.count + 1, gold: sum.gold + reward.gold, forgeDust: sum.forgeDust + reward.forgeDust };
    },
    { count: 0, gold: 0, forgeDust: 0 },
  );
}

/** Pulls new drops that fall under the server auto-recycle line. Off leaves every drop. */
export function partitionNewDrops(data: SaveData, incoming: Item[]): { keep: Item[]; gold: number; forgeDust: number; recycled: number } {
  const line = autoRecycleLine(data);
  if (!line) return { keep: incoming, gold: 0, forgeDust: 0, recycled: 0 };
  const keep: Item[] = [];
  let gold = 0;
  let forgeDust = 0;
  let recycled = 0;
  for (const item of incoming) {
    if (RARITY_RANK[item.rarity] < RARITY_RANK[line] && canRecycle(item, data.gear.equipped)) {
      const reward = recycleReward(item);
      gold += reward.gold;
      forgeDust += reward.forgeDust;
      recycled += 1;
    } else {
      keep.push(item);
    }
  }
  return { keep, gold, forgeDust, recycled };
}

export function absorbDrops(data: SaveData, incoming: Item[]): { bag: Item[]; gold: number; forgeDust: number; recycled: number } {
  const settled = partitionNewDrops(data, incoming);
  const bag = addToBag(data.gear.bag, settled.keep);
  return {
    bag: bag.bag,
    gold: settled.gold + bag.gold,
    forgeDust: settled.forgeDust + bag.dust,
    recycled: settled.recycled,
  };
}

export function equipItem(equipped: EquippedGear, bag: Item[], itemId: string): { equipped: EquippedGear; bag: Item[] } | null {
  const index = bag.findIndex((item) => item.id === itemId);
  if (index < 0) return null;
  const item = bag[index]!;
  const slot: GearSlot = item.slot;
  const previous = equipped[slot];
  const nextBag = bag.filter((candidate) => candidate.id !== itemId);
  if (previous) nextBag.push(previous);
  return {
    equipped: { ...equipped, [slot]: item },
    bag: nextBag,
  };
}

export function unequipItem(equipped: EquippedGear, bag: Item[], slot: GearSlot): { equipped: EquippedGear; bag: Item[] } | null {
  const item = equipped[slot];
  if (!item) return null;
  return {
    equipped: { ...equipped, [slot]: null },
    bag: [...bag, item],
  };
}
